import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TextDescription, TextTitle, Text } from "components/Texts";

import Column from "components/Column";
import Dropdown from "components/Dropdown";
import Row from "components/Row";
import Section from "components/Section";
import widgetConversions from "modules/utils/widgetConversions";
import _forEach from "lodash/forEach";
import _find from "lodash/find";
import _groupBy from "lodash/groupBy";
import _get from "lodash/get";
import _assign from "lodash/assign";
import colors from "styles/colors";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { TrendingLineGraph, initGraphLabel, initGraphData } from "./TrendingLineGraph";
import googleConversions from "modules/utils/googleSheetsConversions";

const TrendingGraph1 = ({ dataList, view, selectedViewValue }) => {
  const rmpToken = sessionStorage.getItem("rmp_token");
  const [t] = useTranslation();
  const [tooltip, setTooltip] = useState();
  const [metric, setMetric] = useState("all");
  const [labelDate, setLabelDate] = useState([]);
  const [newDataList, setNewDataList] = useState([]);
  const [hasDataListValue, setHasDataListValue] = useState(false);

  const [totalConversions, setTotalConversions] = useState("");
  const [totalConversionValues, setTotalConversionValues] = useState("");
  const [diffTotalConversions, setDiffTotalConversions] = useState("");
  const [diffTotalConversionValues, setDiffTotalConversionValues] = useState("");

  const widgetConversionsList = widgetConversions.getConversions(t);
  const googleSheetsConversionsList = googleConversions.getConversions(t);
  const widgetConversionsListAdditional = {
    "RWidget-SmartConversions": {
      title: t("__ads_widget_action_behavior"),
      id: 13,
    },
    "RWidget-TotalActions": {
      title: t("__ads_widget_action_total_action"),
      id: 12,
    },
  };

  useEffect(() => {
    setHasDataListValue(false);

    if (dataList[metric]) {
      // Set total data
      setTotalConversions(dataList[metric].totalConversions);
      setTotalConversionValues(dataList[metric].totalConversionValues);
      setDiffTotalConversions(dataList[metric].diffTotalConversions);
      setDiffTotalConversionValues(dataList[metric].diffTotalConversionValues);

      const graphLabel = initGraphLabel(dataList, metric, selectedViewValue);
      setLabelDate(graphLabel);
    } else {
      setTotalConversions(0);
      setTotalConversionValues(0);
      setDiffTotalConversions("0");
      setDiffTotalConversionValues("0");
      setNewDataList([]);
    }
  }, [dataList, metric, selectedViewValue]);

  useEffect(() => {
    if (labelDate.length > 0) {
      setHasDataListValue(false);
      const graphData = initGraphData(dataList, metric, labelDate, selectedViewValue);

      setNewDataList(graphData);
    }
  }, [labelDate]);

  useEffect(() => {
    checkDataListValue();
  }, [newDataList]);

  const checkDataListValue = () => {
    newDataList.map((item) => {
      if (item.value && item.value > 0) {
        setHasDataListValue(true);
      }
    });
  };

  let dropdownItems = [
    {
      title: t("__ads_performance_all_option"),
      key: "all",
    },
  ];

  if (view.title !== "R-Widget" && view.title !== "") {
    // add custom conversion
    for (const key in dataList) {
      if (key.match(/GSheets-(Conversion[1-9]|Conversion10|Initial)/)) {
        if (key in googleSheetsConversionsList) {
          dropdownItems.push({
            title: googleSheetsConversionsList[key].title,
            key: key,
            id: googleSheetsConversionsList[key].id, // id for sorting
          });
        } else {
          dropdownItems.push({
            title: key,
            key: key,
            id: 0, // id for sorting
          });
        }
      }
    }
  } else if (view.title === "R-Widget") {
    let index = 14;
    for (const key in dataList) {
      if (key.match(/RWidget-(.*)/)) {
        if (key in widgetConversionsList) {
          dropdownItems.push({
            title: widgetConversionsList[key].title,
            key: key,
            id: widgetConversionsList[key].id, // id for sorting
          });
        } else if (key in widgetConversionsListAdditional) {
          dropdownItems.push({
            title: widgetConversionsListAdditional[key].title,
            key: key,
            id: widgetConversionsListAdditional[key].id, // id for sorting
          });
        } else {
          dropdownItems.push({
            title: key,
            key: key,
            id: index, // id for sorting
          });
          index++;
        }
      }
    }
  }
  // sort dropdown items by id ascending
  dropdownItems.sort((a, b) => a.id - b.id);

  const onDropdownSelected = (item) => {
    setMetric(item.key);
  };

  const isPositiveNumber = (data) => {
    var value = data;
    if (value === undefined || value === null || value === "" || value === "0" || value == 0 || value === "0.00") {
      data = "0.00";
    }
    if (typeof data !== "number") {
      value = data.replace(",", "");
      value = parseFloat(value);
    }
    return value > 0 ? "+" + data : data;
  };

  const adjustColumn = { display: "flex", gap: "5px", justifyContent: "space-between" };
  const adjustDropdown = { flexGrow: 0, flexShrink: 1, flexBasis: "240px" };
  const adjustTotal = {
    display: "flex",
    justifyContent: "center",
    gap: "20px",
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: "140px",
    marginLeft: "auto",
  };
  return (
    <Section margin="0 0 2px">
      <Row margin="0px 0px 35px">
        <Column>
          <ColumnCard style={adjustColumn}>
            <div style={adjustDropdown} className="dropdown-card">
              <Text size="12px">{t("__fbads_dashboard_lead_conversion_name")}</Text>
              <Dropdown items={dropdownItems} minWidth="100%" onSelected={onDropdownSelected} />
            </div>

            <div style={adjustTotal} className="dropdown-total">
              <div>
                <Text size="12px" align="center">
                  {t("__fbads_dashboard_lead_conversion_total")}
                </Text>
                <Text size="12px" align="center">
                  <b>
                    {totalConversions === undefined ||
                    totalConversions === null ||
                    totalConversions === "" ||
                    totalConversions === "0" ||
                    totalConversions == 0 ||
                    totalConversions === "0.00"
                      ? "0.00"
                      : totalConversions}
                  </b>
                </Text>
                {diffTotalConversions === "N/A" ? (
                  <Text size="10px" align="center" color="#A8A8A8">
                    N/A
                  </Text>
                ) : (
                  <Text
                    size="10px"
                    align="center"
                    color={
                      diffTotalConversions.indexOf("-") < 0
                        ? colors.positive
                        : diffTotalConversions.indexOf("-") >= 0
                          ? colors.negative
                          : colors.positive
                    }
                  >
                    {isPositiveNumber(diffTotalConversions)}%
                  </Text>
                )}
              </div>
              <div>
                <Text size="12px" align="center">
                  {t("__fbads_dashboard_lead_conversion_value")}
                </Text>
                <Text size="12px" align="center">
                  <b>
                    {totalConversionValues === undefined ||
                    totalConversionValues === null ||
                    totalConversionValues === "" ||
                    totalConversionValues === "0" ||
                    totalConversionValues == 0 ||
                    totalConversionValues === "0.00"
                      ? "0.00"
                      : totalConversionValues}
                  </b>
                </Text>
                {diffTotalConversionValues === "N/A" ? (
                  <Text size="10px" align="center" color="#A8A8A8">
                    N/A
                  </Text>
                ) : (
                  <Text
                    size="10px"
                    align="center"
                    color={
                      diffTotalConversionValues.indexOf("-") < 0
                        ? colors.positive
                        : diffTotalConversionValues.indexOf("-") >= 0
                          ? colors.negative
                          : colors.positive
                    }
                  >
                    {isPositiveNumber(diffTotalConversionValues)}%
                  </Text>
                )}
              </div>
            </div>
          </ColumnCard>
        </Column>
      </Row>

      <BarZone>
        {hasDataListValue === true ? (
          <>
            <TrendingLineGraph setTooltip={setTooltip} dataList={newDataList} metric={metric} labels={labelDate} />
            {tooltip}
          </>
        ) : (
          <>
            <StyledEmptyContainer>
              <p>{t("__no_data")}</p>
            </StyledEmptyContainer>
          </>
        )}
      </BarZone>
    </Section>
  );
};

export default TrendingGraph1;

const BarZone = styled.div`
  height: 240px;
  position: relative;
`;
const StyledEmptyContainer = styled.div`
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.descriptionText};
`;

const ManageColumn = styled(Column)`
  flex-grow: 1;
  flex-shrink: 1;
  min-width: 70px !important;
`;

const ManageRow = styled(Row)`
  justify-content: space-between;
`;

const ManageColumnWidth = styled(Column)`
  min-width: 100% !important;
`;

const AdjustTotal = styled.div`
  display: flex;
  margin-left: 100px;
  @media screen and (max-width: 834px) {
    margin-left: 50px !important;
  }
`;

const ColumnCard = styled.div`
  @media screen and (min-width: 320px) and (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;

    .dropdown-card {
      flex: none !important;
      width: 100%;
      margin: 10px 0;
    }

    .dropdown-total {
      justify-content: flex-start;
      align-items: flex-start;
      flex-basis: 20px !important;
      margin-left: 0 !important;
    }
  }
`;
